// src/pages/RestablecerContrasena.js
// =========================================================================
// Elegir contraseña con el enlace del correo (recuperación o bienvenida).
// El token llega en la dirección (?token=…) y solo vale una vez.
// =========================================================================
import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { LockOutlined, Visibility, VisibilityOff } from '@mui/icons-material';
import api from '../services/api';
import TarjetaAcceso, { Aviso, estiloBoton, estiloEntrada } from '../components/auth/TarjetaAcceso';

// Misma política que el servidor (shared/schemas/auth.schema.js · passwordSegura)
const problemaPassword = (p) => {
    if (p.length < 8) return 'Al menos 8 caracteres.';
    if (!/[A-Za-z]/.test(p)) return 'Debe tener al menos una letra.';
    if (!/[0-9]/.test(p)) return 'Debe tener al menos un número.';
    return null;
};

export default function RestablecerContrasena() {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const token = params.get('token') || '';
    const tokenValido = /^[0-9a-f]{64}$/.test(token);

    const [password, setPassword] = useState('');
    const [repetida, setRepetida] = useState('');
    const [ver, setVer] = useState(false);
    const [guardando, setGuardando] = useState(false);
    const [error, setError] = useState('');
    const [hecho, setHecho] = useState(false);

    const problema = password ? problemaPassword(password) : null;
    const noCoinciden = repetida && repetida !== password;

    const guardar = async (e) => {
        e.preventDefault();
        const fallo = problemaPassword(password) || (password !== repetida ? 'Las dos contraseñas no coinciden.' : null);
        if (fallo) return setError(fallo);
        setError('');
        setGuardando(true);
        try {
            await api.post('/auth/restablecer', { token, password });
            setHecho(true);
        } catch (err) {
            setError(err.message || 'No se ha podido cambiar la contraseña.');
        } finally {
            setGuardando(false);
        }
    };

    if (!tokenValido) {
        return (
            <TarjetaAcceso titulo="Enlace no válido">
                <Aviso>El enlace está incompleto o no es correcto. Ábrelo de nuevo desde el correo o pide uno nuevo.</Aviso>
                <Link to="/recuperar-contrasena" className={`${estiloBoton} block text-center`}>Pedir un enlace nuevo</Link>
            </TarjetaAcceso>
        );
    }

    if (hecho) {
        return (
            <TarjetaAcceso titulo="Contraseña cambiada">
                <Aviso tipo="ok">Ya puedes entrar con tu contraseña nueva. Por seguridad, se han cerrado todas tus sesiones abiertas.</Aviso>
                <button type="button" onClick={() => navigate('/login', { replace: true })} className={estiloBoton}>Ir al acceso</button>
            </TarjetaAcceso>
        );
    }

    const campo = (id, valor, cambiar, texto) => (
        <div className="relative group">
            <div className="absolute inset-y-0 left-0 flex items-center pl-4 pointer-events-none text-slate-500 group-focus-within:text-sky-400 transition-colors">
                <LockOutlined fontSize="small" />
            </div>
            <input
                id={id} type={ver ? 'text' : 'password'} required autoComplete="new-password"
                aria-label={texto} placeholder={texto}
                value={valor} onChange={(e) => cambiar(e.target.value)}
                className={estiloEntrada}
            />
        </div>
    );

    return (
        <TarjetaAcceso titulo="Elige tu contraseña" subtitulo="Mínimo 8 caracteres, con letras y números.">
            {error && <Aviso>{error}</Aviso>}
            <form onSubmit={guardar} className="space-y-4" noValidate>
                {campo('pass-nueva', password, setPassword, 'Contraseña nueva')}
                {problema && <p className="-mt-2 ml-2 text-[11px] font-bold text-amber-400">{problema}</p>}
                {campo('pass-repetida', repetida, setRepetida, 'Repite la contraseña')}
                {noCoinciden && <p className="-mt-2 ml-2 text-[11px] font-bold text-amber-400">Las dos contraseñas no coinciden.</p>}
                <button type="button" onClick={() => setVer(v => !v)} className="flex items-center gap-1.5 ml-2 text-xs font-bold text-slate-400 hover:text-white">
                    {ver ? <VisibilityOff sx={{ fontSize: 16 }} /> : <Visibility sx={{ fontSize: 16 }} />}
                    {ver ? 'Ocultar contraseñas' : 'Mostrar contraseñas'}
                </button>
                <button type="submit" disabled={guardando || Boolean(problema) || !password || password !== repetida} className={estiloBoton}>
                    {guardando ? 'Guardando…' : 'Guardar contraseña'}
                </button>
            </form>
            <div className="mt-8 pt-4 border-t border-white/5 text-center">
                <Link to="/login" className="text-xs font-bold text-sky-400 hover:text-sky-300">Volver al acceso</Link>
            </div>
        </TarjetaAcceso>
    );
}
